import { cn } from '../../../lib/utils';

export type CharlieSkeletonProps = React.HTMLAttributes<HTMLDivElement>;

/**
 * CharlieSkeleton — An animated placeholder block for loading states.
 *
 * Renders a rounded, pulsing grey block. Use `className` to set width/height.
 *
 * Matches the [ShadCN Skeleton](https://ui.shadcn.com/docs/components/skeleton) pattern.
 *
 * ## Usage
 *
 * ```tsx
 * import { CharlieSkeleton } from '@ver.id/libs.ui.charlie';
 *
 * // Text placeholder
 * <CharlieSkeleton className="h-4 w-48" />
 *
 * // Avatar placeholder
 * <CharlieSkeleton className="size-10 rounded-full" />
 *
 * // Card placeholder
 * <CharlieSkeleton className="h-[207px] w-full rounded-md" />
 *
 * // Compose a skeleton group
 * <div className="flex items-center gap-4">
 *   <CharlieSkeleton className="size-10 rounded-full" />
 *   <div className="flex flex-col gap-2">
 *     <CharlieSkeleton className="h-4 w-[150px]" />
 *     <CharlieSkeleton className="h-4 w-[100px]" />
 *   </div>
 * </div>
 * ```
 *
 * ## Gotchas
 *
 * - You **must** provide dimensions via `className` — the component has no
 *   default width or height.
 * - Uses `animate-pulse` for the loading animation. This can be overridden
 *   with a custom animation class.
 */
export function CharlieSkeleton({ className, ...props }: CharlieSkeletonProps) {
  return (
    <div
      data-slot="skeleton"
      className={cn('animate-pulse rounded-md bg-[#e5e5e5]', className)}
      {...props}
    />
  );
}
